import { useEffect, useState } from "react";
import { Globe, Loader2, Unplug, Link2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const fieldCls = "rounded-xl border-brand-line focus-visible:ring-brand-red/30 focus-visible:border-brand-navy";
const RANGES = [
  { days: 7, label: "7 days" },
  { days: 28, label: "28 days" },
  { days: 90, label: "90 days" },
];

const num = (n) => (n == null ? "—" : new Intl.NumberFormat("en-US").format(Math.round(n)));

const Delta = ({ value }) => {
  if (value == null) return <span className="text-xs font-bold text-brand-ink2">—</span>;
  if (value === 0) return <span className="inline-flex items-center gap-1 text-xs font-bold text-brand-ink2"><Minus className="w-3.5 h-3.5" /> 0</span>;
  const good = value > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold ${good ? "text-emerald-600" : "text-red-600"}`}>
      {good ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
      {good ? "+" : ""}{value}
    </span>
  );
};

function StatTile({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-brand-bg border border-brand-line p-3 text-center" data-testid={testid}>
      <p className="text-xl font-display font-bold text-brand-navy">{value}</p>
      <p className="text-xs font-semibold text-brand-ink2 mt-0.5">{label}</p>
    </div>
  );
}

function RowsTable({ title, rows, keyLabel, testid }) {
  if (!rows?.length) return null;
  return (
    <div data-testid={testid}>
      <p className="font-bold text-brand-navy text-sm mb-2">{title}</p>
      <div className="rounded-xl border border-brand-line overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-brand-bg text-brand-navy text-xs">
              <th className="text-left font-bold px-3 py-2">{keyLabel}</th>
              <th className="text-right font-bold px-3 py-2">Clicks</th>
              <th className="text-right font-bold px-3 py-2">Impr.</th>
              <th className="text-right font-bold px-3 py-2">CTR</th>
              <th className="text-right font-bold px-3 py-2">Pos.</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 8).map((r, i) => (
              <tr key={i} className="border-t border-brand-line">
                <td className="px-3 py-2 font-semibold text-brand-navy truncate max-w-[220px]">{r.key}</td>
                <td className="px-3 py-2 text-right text-brand-ink2">{num(r.clicks)}</td>
                <td className="px-3 py-2 text-right text-brand-ink2">{num(r.impressions)}</td>
                <td className="px-3 py-2 text-right text-brand-ink2">{(r.ctr * 100).toFixed(1)}%</td>
                <td className="px-3 py-2 text-right text-brand-ink2">{r.position.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function GscConnection({ conn, onChanged }) {
  const [property, setProperty] = useState("");
  const [saJson, setSaJson] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const [days, setDays] = useState(28);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [keywords, setKeywords] = useState(null);

  useEffect(() => {
    if (!conn) { setKeywords(null); return; }
    api.get("/admin/connections/gsc/keywords")
      .then(({ data }) => setKeywords(data))
      .catch(() => setKeywords(null));
  }, [conn]);

  useEffect(() => {
    if (!conn) { setStats(null); return; }
    setLoading(true);
    setStatsError(null);
    api.get(`/admin/connections/gsc/stats?days=${days}`)
      .then(({ data }) => setStats(data))
      .catch((err) => setStatsError(formatApiError(err.response?.data?.detail, "Could not load Search Console data.")))
      .finally(() => setLoading(false));
  }, [conn, days]);

  const connect = async () => {
    setConnecting(true);
    setConnectError(null);
    try {
      const { data } = await api.post("/admin/connections/gsc", {
        property_url: property.trim(),
        service_account_json: saJson,
      });
      setSaJson("");
      toast.success(`Search Console connected (${data.service_account_email})`);
      onChanged();
    } catch (err) {
      setConnectError(formatApiError(err.response?.data?.detail, "Could not connect Search Console."));
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.delete("/admin/connections/gsc");
      toast.success("Search Console disconnected");
      onChanged();
    } catch {
      toast.error("Could not disconnect.");
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-brand-line p-5 shadow-card" data-testid="gsc-connection">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand-navy"><Globe className="w-5 h-5 text-white" strokeWidth={2.2} /></span>
          <div>
            <h2 className="font-display font-bold text-lg text-brand-navy leading-tight">Google Search Console</h2>
            <p className="text-sm text-brand-ink2">{conn ? `Connected · ${conn.property_url}` : "Real clicks, impressions & keyword rankings"}</p>
          </div>
        </div>
        {conn && (
          <button onClick={disconnect} data-testid="gsc-disconnect"
            className="btn-focus inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-brand-line text-sm font-semibold text-brand-red hover:bg-brand-red/8 transition-colors">
            <Unplug className="w-3.5 h-3.5" /> Disconnect
          </button>
        )}
      </div>

      {!conn ? (
        <div className="space-y-4" data-testid="gsc-connect-form">
          <ol className="list-decimal pl-5 text-sm text-brand-ink2 space-y-1">
            <li>In Google Cloud Console, enable the <span className="font-semibold text-brand-navy">Search Console API</span> and create a Service Account with a JSON key.</li>
            <li>In Search Console → Settings → <span className="font-semibold text-brand-navy">Users and permissions</span>, add the service-account email as a user.</li>
            <li>Paste your property and the JSON key below.</li>
          </ol>
          <div className="space-y-2">
            <Label className="text-brand-navy font-semibold">Search Console property</Label>
            <Input data-testid="gsc-property-input" value={property} onChange={(e) => setProperty(e.target.value)}
              placeholder="sc-domain:lifebutton.org or https://lifebutton.org/" className={`h-11 ${fieldCls}`} />
          </div>
          <div className="space-y-2">
            <Label className="text-brand-navy font-semibold">Service-account JSON key</Label>
            <Textarea data-testid="gsc-json-input" value={saJson} onChange={(e) => setSaJson(e.target.value)}
              placeholder='Paste the full JSON key file — {"type": "service_account", ...}' className={`min-h-[110px] font-mono text-xs ${fieldCls}`} />
            <p className="text-xs text-brand-ink2">Stored encrypted. Never shown again after connecting.</p>
          </div>
          {connectError && <p className="text-sm font-semibold text-brand-red" data-testid="gsc-connect-error">{connectError}</p>}
          <button onClick={connect} disabled={connecting || !property.trim() || !saJson.trim()} data-testid="gsc-connect-btn"
            className="btn-focus inline-flex items-center gap-2 h-11 px-5 rounded-full bg-brand-navy text-white font-bold hover:bg-brand-navy-700 transition-colors disabled:opacity-60">
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            {connecting ? "Verifying with Google…" : "Connect Search Console"}
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="gsc-stats">
          <div className="flex items-center gap-1.5">
            {RANGES.map((r) => (
              <button key={r.days} onClick={() => setDays(r.days)} data-testid={`gsc-range-${r.days}`}
                className={`btn-focus h-8 px-3 rounded-full text-xs font-bold transition-colors ${days === r.days ? "bg-brand-navy text-white" : "border border-brand-line text-brand-navy hover:bg-brand-bg"}`}>
                {r.label}
              </button>
            ))}
            {stats && <span className="text-xs text-brand-ink2 ml-auto">{stats.start_date} → {stats.end_date}</span>}
          </div>
          {loading ? (
            <div className="h-32 grid place-items-center"><Loader2 className="w-6 h-6 text-brand-red animate-spin" /></div>
          ) : statsError ? (
            <p className="text-sm font-semibold text-brand-red" data-testid="gsc-stats-error">{statsError}</p>
          ) : stats ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                <StatTile label="Clicks" value={num(stats.totals?.clicks)} testid="gsc-clicks" />
                <StatTile label="Impressions" value={num(stats.totals?.impressions)} testid="gsc-impressions" />
                <StatTile label="Avg CTR" value={`${((stats.totals?.ctr || 0) * 100).toFixed(1)}%`} testid="gsc-ctr" />
                <StatTile label="Avg position" value={(stats.totals?.position || 0).toFixed(1)} testid="gsc-position" />
              </div>
              {(stats.top_queries?.length || stats.top_pages?.length) ? (
                <div className="space-y-4">
                  <RowsTable title="Top keyword queries" rows={stats.top_queries} keyLabel="Query" testid="gsc-queries" />
                  <RowsTable title="Top pages" rows={stats.top_pages} keyLabel="Page" testid="gsc-pages" />
                </div>
              ) : (
                <p className="text-sm text-brand-ink2" data-testid="gsc-no-data">No search data for this range yet — Google usually needs a few days after a site goes live.</p>
              )}
              {keywords?.rows?.length > 0 && (
                <div data-testid="gsc-keyword-tracker">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                    <p className="font-bold text-brand-navy text-sm">Keyword tracker — week over week</p>
                    <span className="text-[11px] text-brand-ink2">{keywords.current_range.start} → {keywords.current_range.end} vs prior week</span>
                  </div>
                  <div className="rounded-xl border border-brand-line overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-brand-bg text-brand-navy text-xs">
                          <th className="text-left font-bold px-3 py-2">Keyword</th>
                          <th className="text-right font-bold px-3 py-2">Clicks</th>
                          <th className="text-right font-bold px-3 py-2">Change</th>
                          <th className="text-right font-bold px-3 py-2">Position</th>
                          <th className="text-right font-bold px-3 py-2">Movement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keywords.rows.slice(0, 10).map((k, i) => (
                          <tr key={i} className="border-t border-brand-line" data-testid={`gsc-keyword-row-${i}`}>
                            <td className="px-3 py-2 font-semibold text-brand-navy truncate max-w-[200px]">
                              {k.query}
                              {k.is_new && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase align-middle">New</span>}
                            </td>
                            <td className="px-3 py-2 text-right text-brand-ink2">{num(k.clicks)}</td>
                            <td className="px-3 py-2 text-right"><Delta value={k.is_new ? null : k.clicks_delta} /></td>
                            <td className="px-3 py-2 text-right text-brand-ink2">{k.position.toFixed(1)}</td>
                            <td className="px-3 py-2 text-right"><Delta value={k.position_delta} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-brand-ink2 mt-1.5">Movement = change in average Google position vs the prior week (green up = ranking higher).</p>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
